/**
 * Admin-only "rebuild & reload" control for the DESKTOP-SERVED webapp.
 *
 * The app is served on the PC by `vite preview` from the built dist/ folder and
 * reached from the phone over Tailscale. The PC's git clone only gets new code
 * when someone runs `git pull` on the machine. This control lets Edd trigger
 * that from the phone: the collector pulls the latest pushed code and rebuilds,
 * then the page reloads onto the fresh dist.
 *
 * It is deliberately out of the way — an admin tool, not a student feature — so
 * it only ever appears when ALL of these hold:
 *   - we're running as WEB, not the packaged Capacitor APK (the APK has no PC
 *     server to talk to; it updates via the separate APK updater), and
 *   - a collector base URL is configured (otherwise there are no /update
 *     endpoints to reach), and
 *   - admin mode is on (the soft, persisted gate in src/store/admin.ts).
 * When any of those is false it renders nothing and does no polling. An
 * unreachable PC is handled quietly: no banner, no error noise.
 */
import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAdminStore } from '@/store/admin';
import { useAppUpdateStore } from '@/store/appUpdate';
import { updateConfigured } from '@/feedback/updateClient';
import { haptic } from '@/ui/haptics';

const STATUS_POLL_MS = 60_000; // check for new commits about once a minute
const PROGRESS_POLL_MS = 1_500; // while a rebuild runs, poll its log/phase

function phaseLabel(phase: string): string {
  switch (phase) {
    case 'fetching':
      return 'Checking for changes…';
    case 'pulling':
      return 'Pulling the latest code…';
    case 'building':
      return 'Rebuilding the app…';
    case 'done':
      return 'Done — reloading…';
    default:
      return 'Working…';
  }
}

export function RebuildControl() {
  // These don't change during a session, so plain reads are fine.
  const active = useAdminStore((s) => s.adminMode) && !Capacitor.isNativePlatform() && updateConfigured();

  const status = useAppUpdateStore((s) => s.status);
  const reachable = useAppUpdateStore((s) => s.reachable);
  const running = useAppUpdateStore((s) => s.running);
  const phase = useAppUpdateStore((s) => s.phase);
  const tail = useAppUpdateStore((s) => s.tail);
  const error = useAppUpdateStore((s) => s.error);
  const refresh = useAppUpdateStore((s) => s.refresh);
  const startRebuild = useAppUpdateStore((s) => s.startRebuild);
  const pollProgress = useAppUpdateStore((s) => s.pollProgress);

  const [showLog, setShowLog] = useState(false);

  // Status polling: on mount, every 60s, and whenever the app regains focus —
  // only while the control is active. Cleaned up when it isn't.
  useEffect(() => {
    if (!active) return;
    void refresh();
    const id = window.setInterval(() => void refresh(), STATUS_POLL_MS);
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [active, refresh]);

  // Progress polling: only while a rebuild is actually running.
  useEffect(() => {
    if (!active || !running) return;
    void pollProgress();
    const id = window.setInterval(() => void pollProgress(), PROGRESS_POLL_MS);
    return () => window.clearInterval(id);
  }, [active, running, pollProgress]);

  if (!active) return null;

  // A rebuild is in flight: show the phase, a spinner, and a peek at the log.
  if (running) {
    return (
      <div className="acp-rebuild-banner running" role="status" aria-live="polite">
        <span className="acp-rebuild-spin" aria-hidden="true" />
        <span className="acp-rebuild-text">
          {phaseLabel(phase)}
          {tail.length > 0 && (
            <button type="button" className="acp-rebuild-loglink" onClick={() => setShowLog((v) => !v)}>
              {showLog ? 'hide log' : 'show log'}
            </button>
          )}
        </span>
        {showLog && tail.length > 0 && (
          <pre className="acp-rebuild-log">{tail.join('\n')}</pre>
        )}
      </div>
    );
  }

  // A failed (or un-startable) rebuild: surface it with a dismiss.
  if (error) {
    return (
      <div className="acp-rebuild-banner error" role="alert">
        <span className="acp-rebuild-text">Rebuild failed: {error}</span>
        {tail.length > 0 && (
          <button type="button" className="acp-rebuild-loglink" onClick={() => setShowLog((v) => !v)}>
            {showLog ? 'hide log' : 'show log'}
          </button>
        )}
        <button
          type="button"
          className="acp-rebuild-btn"
          onClick={() => {
            useAppUpdateStore.setState({ error: null });
            setShowLog(false);
          }}
        >
          Dismiss
        </button>
        {showLog && tail.length > 0 && <pre className="acp-rebuild-log">{tail.join('\n')}</pre>}
      </div>
    );
  }

  // Up to date, or the PC is off: show nothing (this is a tucked-away tool).
  if (!reachable || !status?.updateAvailable) return null;

  const n = status.behind;
  return (
    <div className="acp-rebuild-banner" role="status">
      <span className="acp-rebuild-dot" aria-hidden="true" />
      <span className="acp-rebuild-text">
        Update available
        <span className="acp-rebuild-sub">
          {n} commit{n === 1 ? '' : 's'} behind
        </span>
      </span>
      <button
        type="button"
        className="acp-rebuild-btn"
        onClick={() => {
          haptic('commit');
          void startRebuild();
        }}
      >
        Rebuild &amp; reload
      </button>
    </div>
  );
}
